import React, { useState, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link,
  InputAdornment,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Email as EmailIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  MarkEmailRead as MarkEmailReadIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

const ForgotPassword: React.FC = () => {
  // Detect branded domain (same logic as Login.tsx)
  const brandedDomain = useMemo(() => {
    const host = window.location.hostname;
    if (!host || host === 'localhost' || host === '127.0.0.1') return '';
    if (host === 'cruvzmail.com' || host.endsWith('.cruvzmail.com')) return '';
    const parts = host.split('.');
    const mailSubdomains = ['mail', 'webmail', 'smtp', 'imap', 'pop', 'mx', 'm', 'email'];
    if (parts.length > 2 && mailSubdomains.includes(parts[0].toLowerCase())) {
      return parts.slice(1).join('.');
    }
    return host;
  }, []);

  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // null = not submitted yet; string = success with masked email (may be empty string if none configured)
  const [maskedEmail, setMaskedEmail] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!input.trim()) {
      setError(brandedDomain ? 'Please enter your recovery email address' : 'Please enter your email address');
      return;
    }

    // Basic email format check
    if (!input.includes('@') || !input.includes('.')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsLoading(true);
    try {
      let payload: Record<string, string>;
      if (brandedDomain) {
        // Branded domain: user enters their recovery email (external email used at signup OTP)
        // Backend looks up the org by that recovery email + loginDomain
        payload = {
          recoveryEmail: input.trim(),
          loginDomain: brandedDomain,
          portalOrigin: window.location.origin,
          email: input.trim(), // DTO requires email, set same value
        };
      } else {
        payload = { email: input.trim() };
      }

      const res = await api.post('/auth/forgot-password', payload);
      // TransformInterceptor wraps data as { success, data: {...}, timestamp }
      // so maskedEmail may be at res.data.data.maskedEmail or res.data.maskedEmail
      const masked = res.data?.data?.maskedEmail || res.data?.maskedEmail;
      if (masked) {
        setMaskedEmail(masked);
      } else {
        // Account exists but no recovery email configured
        setError('No recovery email is configured for this account. Please contact your administrator.');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // ── Success screen ─────────────────────────────────────────────────────────
  if (maskedEmail !== null) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', p: 2 }}>
        <Paper elevation={8} sx={{ maxWidth: 440, width: '100%', p: 4, borderRadius: 3, textAlign: 'center' }}>
          <Box sx={{ width: 80, height: 80, borderRadius: '50%', bgcolor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
            <MarkEmailReadIcon sx={{ color: '#16a34a', fontSize: 44 }} />
          </Box>

          <Typography variant="h5" fontWeight={700} mb={1.5}>
            Check Your Email
          </Typography>

          <Typography variant="body2" color="text.secondary" mb={3} lineHeight={1.7}>
            We've sent password reset instructions to your recovery address:
          </Typography>

          <Box sx={{ py: 1.5, px: 3, bgcolor: '#f1f5f9', borderRadius: 2, mb: 3, display: 'inline-block' }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#1e293b', fontSize: '1rem', letterSpacing: '0.05em' }}>
              {maskedEmail}
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" mb={4} lineHeight={1.7}>
            Open that email and click the <strong>Reset My Password</strong> link. The link expires in <strong>1 hour</strong>.
          </Typography>

          <Button component={RouterLink} to="/login" variant="contained" fullWidth size="large" sx={{ py: 1.5, fontWeight: 600 }}>
            Back to Sign In
          </Button>

          <Typography variant="body2" color="text.secondary" mt={3}>
            Didn't receive it?{' '}
            <Link component="button" onClick={() => { setMaskedEmail(null); setInput(''); setError(''); }} sx={{ fontWeight: 600, textDecoration: 'none' }}>
              Try again
            </Link>
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ── Form screen ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)', p: 2 }}>
      <Paper elevation={8} sx={{ maxWidth: 420, width: '100%', p: 4, borderRadius: 3 }}>
        <Link component={RouterLink} to="/login" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, textDecoration: 'none', color: 'text.secondary', mb: 3, '&:hover': { color: 'primary.main' } }}>
          <ArrowBackIcon fontSize="small" />
          <Typography variant="body2">Back to Sign In</Typography>
        </Link>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" fontWeight={700} color="text.primary" mb={1}>
            Forgot Password?
          </Typography>
          <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
            {brandedDomain
              ? `Enter the recovery email you used during ${brandedDomain} signup. We'll send a reset link there.`
              : "Enter your email address and we'll send reset instructions to your registered recovery email."}
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <form onSubmit={handleSubmit}>
          {brandedDomain ? (
            <TextField
              fullWidth
              label="Recovery Email"
              type="email"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Enter your recovery email"
              autoComplete="email"
              helperText={`The external email used when you first registered ${brandedDomain}`}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
          ) : (
            <TextField
              fullWidth
              label="Email Address"
              type="email"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="you@yourdomain.com"
              autoComplete="email"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
          )}

          <Button type="submit" variant="contained" fullWidth size="large" disabled={isLoading} sx={{ py: 1.5, fontWeight: 600, fontSize: '1rem' }}>
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
          </Button>
        </form>
      </Paper>
    </Box>
  );
};

export default ForgotPassword;

